import React from 'react';
import { UnscheduledEvent } from '../../types';
import { CAT_MANUAL_DUE_DATE, DUE_DATE_CHANGE_REASONS } from '../../constants';
import { ErtsHistoryEntry } from './saveHelpers';

/**
 * Props for {@link DueDateSection}.
 */
interface DueDateSectionProps {
  formData: UnscheduledEvent;
  mode: 'view' | 'edit' | 'create';
  isReadOnly: boolean;
  updateField: (field: keyof UnscheduledEvent, value: string | number | boolean) => void;
  /**
   * Due Date revision log (reuses the ERTS-history array/table, which is
   * record-type-agnostic). For Deferred records each entry is a logged Due Date.
   */
  dueDateHistory: ErtsHistoryEntry[];
  isDueDateHistoryOpen: boolean;
  setIsDueDateHistoryOpen: React.Dispatch<React.SetStateAction<boolean>>;
  /** Change reason selected for an in-progress Due Date change (edit only). */
  dueDateChangeReason: string;
  setDueDateChangeReason: React.Dispatch<React.SetStateAction<string>>;
  dueDateChangeReasonOther: string;
  setDueDateChangeReasonOther: React.Dispatch<React.SetStateAction<string>>;
  /** The Due Date (YYYY-MM-DD) when the modal opened, to detect a change. */
  openingDueDate: string;
}

/** Extracts the YYYY-MM-DD portion of a stored due-date value. */
function dueDatePart(value: string | undefined): string {
  return value ? value.split(' ')[0] : '';
}

/**
 * Renders the Deferred Due Date field (with clear + Extension), the Change
 * Reason prompt shown when an existing Due Date is revised, and the Due Date
 * History collapsible — logged and tracked in the same manner as ERTS.
 */
const DueDateSection: React.FC<DueDateSectionProps> = ({
  formData,
  mode,
  isReadOnly,
  updateField,
  dueDateHistory,
  isDueDateHistoryOpen,
  setIsDueDateHistoryOpen,
  dueDateChangeReason,
  setDueDateChangeReason,
  dueDateChangeReasonOther,
  setDueDateChangeReasonOther,
  openingDueDate,
}) => {
  const currentDueDate = dueDatePart(formData.dueDateZulu);
  const manualRequired =
    !isReadOnly && CAT_MANUAL_DUE_DATE.includes(formData.catType || '') && !currentDueDate;
  // A change reason is captured when an already-established Due Date is revised
  // in an edit session (mirrors the ERTS change-reason requirement). The first
  // Due Date on a new record is logged automatically as the initial value.
  const dueDateChanged = mode === 'edit' && !!openingDueDate && currentDueDate !== openingDueDate;

  return (
    <>
      <div className="field-row">
        <label className="field-label field-label-vcenter">Due Date</label>
        <div className="field-group due-date-field-group">
          <div className="field-input-block field-input-half">
            <input
              type="date"
              value={currentDueDate}
              readOnly={isReadOnly}
              disabled={isReadOnly}
              onChange={(e) => updateField('dueDateZulu', e.target.value)}
              className={`field-input${manualRequired ? ' field-required-missing' : ''}`}
            />
          </div>
          {!isReadOnly && formData.dueDateZulu && (
            <button
              type="button"
              className="erts-clear-btn"
              onClick={() => updateField('dueDateZulu', '')}
              aria-label="Remove Due Date"
              title="Remove Due Date"
            >
              &times;
            </button>
          )}
          <label className="checkbox-field">
            <input
              type="checkbox"
              checked={!!formData.extension}
              disabled={isReadOnly}
              onChange={(e) => updateField('extension', e.target.checked)}
            />
            Extension
          </label>
          {dueDateChanged && (
            <div className="field-input-block erts-change-reason-block">
              <span className="field-input-label">Change Reason *</span>
              <select
                className={`field-input${!dueDateChangeReason ? ' field-required-missing' : ''}`}
                value={dueDateChangeReason}
                onChange={(e) => {
                  setDueDateChangeReason(e.target.value);
                  if (e.target.value !== 'Other') setDueDateChangeReasonOther('');
                }}
              >
                <option value="">Select Reason...</option>
                {DUE_DATE_CHANGE_REASONS.map((reason) => (
                  <option key={reason} value={reason}>
                    {reason === 'Other' ? 'Other (manual entry)' : reason}
                  </option>
                ))}
              </select>
            </div>
          )}
          {dueDateChanged && dueDateChangeReason === 'Other' && (
            <div className="field-input-block">
              <span className="field-input-label">Other Reason</span>
              <input
                type="text"
                className="field-input"
                value={dueDateChangeReasonOther}
                onChange={(e) => setDueDateChangeReasonOther(e.target.value)}
                placeholder="Enter reason"
              />
            </div>
          )}
        </div>
      </div>

      <div className="collapsible-section">
        <button
          className="collapsible-toggle"
          type="button"
          onClick={() => setIsDueDateHistoryOpen(!isDueDateHistoryOpen)}
        >
          <span className={`toggle-arrow ${isDueDateHistoryOpen ? 'open' : ''}`}>&#9656;</span>
          Due Date History
          {dueDateHistory.length > 0 && (
            <span className="revision-badge">{dueDateHistory.length}</span>
          )}
        </button>
        {isDueDateHistoryOpen && (
          <div className="collapsible-content">
            {dueDateHistory.length === 0 ? (
              <div className="barriers-empty">No Due Date changes recorded</div>
            ) : (
              <div className="erts-history-table due-date-history-table">
                <div className="erts-history-header">
                  <span>Due Date</span>
                  <span>Change Reason</span>
                  <span>Submitted By</span>
                  <span>Submitted At</span>
                </div>
                {[...dueDateHistory]
                  .sort((a, b) => b.submittedAt.localeCompare(a.submittedAt))
                  .map((entry, index) => (
                    <div key={index} className="erts-history-row">
                      <span>{entry.ertsDate}</span>
                      <span>{entry.changeReason}</span>
                      <span>{entry.submittedBy}</span>
                      <span>{entry.submittedAt}</span>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default DueDateSection;
